import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ChevronLeft, SlidersHorizontal, Grid3X3, List, Check } from 'lucide-react';
import { getMachines, getAccessories } from '../../services/productApi';

/**
 * PWA Product List - Premium Industrial Design
 * Unterstützt alle Kategorien: Bagger, Radlader, Gabelstapler, Rüttelplatten, Stampfer, Zubehör
 */
const PWAProductList = ({ 
  category = 'Mini-Bagger', 
  title = 'Bagger', 
  basePath = '/bagger',
  isAccessory = false 
}) => {
  const [products, setProducts] = useState([]);
  const [viewMode, setViewMode] = useState('grid');
  const [loading, setLoading] = useState(true);
  const [sortBy, setSortBy] = useState('default');
  const navigate = useNavigate();

  // Category mapping for different category names in the data
  const categoryMapping = {
    'Mini-Bagger': ['Mini-Bagger'],
    'Radlader': ['Radlader'],
    'Gabelstapler': ['Gabelstapler'],
    'Rüttelplatten': ['Rüttelplatte', 'Rüttelplatten'],
    'Stampfer': ['Stampfer'],
    'Zubehör': ['Zubehör']
  };

  useEffect(() => {
    const loadProducts = async () => {
      setLoading(true);
      try {
        if (isAccessory) {
          const data = await getAccessories();
          setProducts(data || []);
        } else {
          const data = await getMachines();
          const categoryNames = categoryMapping[category] || [category];
          const filtered = (data || []).filter(m => categoryNames.includes(m.category));
          setProducts(filtered);
        }
      } catch (error) {
        console.error('Error loading products:', error);
        setProducts([]);
      } finally {
        setLoading(false);
      }
    };
    loadProducts();
  }, [category, isAccessory]);

  // Sort products
  const sortedProducts = [...products].sort((a, b) => {
    const getPriceValue = (item) => {
      if (isAccessory) {
        return item.price || 0;
      }
      return parseInt(item.pricing?.buy?.replace(/\D/g, '') || '0');
    };
    
    if (sortBy === 'price-asc') {
      return getPriceValue(a) - getPriceValue(b);
    }
    if (sortBy === 'price-desc') {
      return getPriceValue(b) - getPriceValue(a);
    }
    return 0;
  });

  // Get price display
  const getPrice = (item) => {
    if (isAccessory) {
      return item.price ? `${item.price.toLocaleString('de-DE')} €` : 'Auf Anfrage';
    }
    return item.pricing?.buy || 'Auf Anfrage';
  };

  // Get product link
  const getProductLink = (item) => {
    if (isAccessory) {
      return `/zubehoer/${item.slug}`;
    }
    return `${basePath}/${item.slug}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-gray-300 border-t-red-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white pb-20">
      {/* Header */}
      <header className="bg-white border-b border-gray-100 sticky top-0 z-50">
        <div className="flex items-center h-14 px-4">
          <button 
            onClick={() => navigate('/')}
            className="w-10 h-10 -ml-2 flex items-center justify-center rounded-full active:bg-gray-100"
          >
            <ChevronLeft size={24} className="text-gray-700" />
          </button>
          <h1 className="flex-1 text-center font-bold text-lg text-gray-900">{title}</h1>
          <div className="w-10" />
        </div>
        
        {/* Filter Bar */}
        <div className="flex items-center justify-between px-4 py-2 border-t border-gray-50">
          <div className="flex items-center gap-2">
            <select 
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="text-sm text-gray-700 font-medium px-3 py-1.5 rounded-lg bg-gray-100 border-0 focus:ring-0"
            >
              <option value="default">Sortierung</option>
              <option value="price-asc">Preis aufsteigend</option>
              <option value="price-desc">Preis absteigend</option>
            </select>
          </div>
          <div className="flex items-center bg-gray-100 rounded-lg p-0.5">
            <button
              onClick={() => setViewMode('grid')}
              className={`p-1.5 rounded-md ${viewMode === 'grid' ? 'bg-white shadow-sm' : ''}`}
            >
              <Grid3X3 size={18} className={viewMode === 'grid' ? 'text-gray-900' : 'text-gray-400'} />
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={`p-1.5 rounded-md ${viewMode === 'list' ? 'bg-white shadow-sm' : ''}`}
            >
              <List size={18} className={viewMode === 'list' ? 'text-gray-900' : 'text-gray-400'} />
            </button>
          </div>
        </div>
      </header>

      {/* Results Count */}
      <div className="px-4 py-3 border-b border-gray-100">
        <p className="text-sm text-gray-500">{sortedProducts.length} Produkte</p>
      </div>

      {/* Product Grid */}
      {viewMode === 'grid' ? (
        <div className="p-4 grid grid-cols-2 gap-3">
          {sortedProducts.map((product) => (
            <Link
              key={product.id || product.slug}
              to={getProductLink(product)}
              className="bg-white rounded-xl border border-gray-200 overflow-hidden active:scale-[0.98] transition-transform shadow-sm"
            >
              {/* Availability Badge */}
              <div className="px-3 pt-3">
                <span className="inline-flex items-center text-[10px] font-medium text-green-700 bg-green-50 px-2 py-0.5 rounded">
                  <Check size={10} className="mr-1" />
                  Verfügbar
                </span>
              </div>
              
              {/* Image */}
              <div className="h-28 flex items-center justify-center p-3">
                <img 
                  src={product.image} 
                  alt={product.name}
                  className="max-h-full max-w-full object-contain"
                />
              </div>
              
              {/* Info */}
              <div className="p-3 pt-0 border-t border-gray-50">
                <h3 className="font-bold text-gray-900 text-sm line-clamp-2">{product.name}</h3>
                {product.deliveryTime && (
                  <p className="text-xs text-gray-500 mt-0.5">{product.deliveryTime}</p>
                )}
                {product.compatibility && (
                  <p className="text-xs text-gray-500 mt-0.5 line-clamp-1">{product.compatibility}</p>
                )}
                <div className="mt-2 flex items-baseline gap-1">
                  <span className="text-lg font-bold text-red-600">{getPrice(product)}</span>
                </div>
              </div>
            </Link>
          ))}
        </div>
      ) : (
        <div className="divide-y divide-gray-100">
          {sortedProducts.map((product) => (
            <Link
              key={product.id || product.slug}
              to={getProductLink(product)}
              className="flex p-4 active:bg-gray-50 transition-colors"
            >
              {/* Image */}
              <div className="w-24 h-24 bg-gray-50 rounded-lg flex items-center justify-center flex-shrink-0">
                <img 
                  src={product.image} 
                  alt={product.name}
                  className="max-h-full max-w-full object-contain p-2"
                />
              </div>
              
              {/* Info */}
              <div className="flex-1 ml-4 flex flex-col justify-between py-1">
                <div>
                  <div className="flex items-start justify-between gap-2">
                    <h3 className="font-bold text-gray-900 line-clamp-2">{product.name}</h3>
                    <span className="text-[10px] font-medium text-green-700 bg-green-50 px-2 py-0.5 rounded flex-shrink-0">
                      Verfügbar
                    </span>
                  </div>
                  {product.deliveryTime && (
                    <p className="text-sm text-gray-500 mt-0.5">{product.deliveryTime}</p>
                  )}
                  {product.compatibility && (
                    <p className="text-sm text-gray-500 mt-0.5 line-clamp-1">{product.compatibility}</p>
                  )}
                </div>
                <p className="text-xl font-bold text-red-600">{getPrice(product)}</p>
              </div>
            </Link>
          ))}
        </div>
      )}

      {sortedProducts.length === 0 && (
        <div className="flex flex-col items-center justify-center py-16 px-4">
          <p className="text-gray-500 text-center">Keine Produkte in dieser Kategorie gefunden</p>
          <button 
            onClick={() => navigate('/')}
            className="mt-4 px-4 py-2 bg-red-600 text-white rounded-lg font-medium"
          >
            Zurück zur Startseite
          </button>
        </div>
      )}
    </div>
  );
};

export default PWAProductList;
